import {
  Button,
  InformationBox,
  Input,
  RegularModal,
  toast,
} from '@popsure/dirty-swan';
import { getShareUrl } from 'components/PolicyShareButton/utils/getShareUrl';
import lz from 'lz-string';
import { FormEvent, useEffect, useState } from 'react';
import { GenericQuestionnaireState } from 'reducers/genericQuestionnaire';
import { useSafeTranslation } from 'shared/i18n';
import { emailAddressValidator } from 'shared/util/emailValidator';

import { useGetSeedId } from '../../hooks/useGetSeedId';
import illustration from './assets/illustration.svg';

type SendToCustomerButtonProps = {
  affiliateId: string;
  affiliateSource?: string;
  questionnaireAnswers: Record<string, unknown>;
  verticalId: keyof GenericQuestionnaireState;
};

export const SendToCustomerButton = ({
  affiliateId,
  affiliateSource,
  questionnaireAnswers,
  verticalId,
}: SendToCustomerButtonProps) => {
  const { t } = useSafeTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [invalidEmail, setInvalidEmail] = useState(true);
  const { isLoading, error, mutateAsync } = useGetSeedId();

  useEffect(() => {
    setInvalidEmail(!emailAddressValidator(email));
  }, [email]);

  const getUrl = async (emailValue?: string) => {
    const data = await mutateAsync({
      path: window.location.pathname,
      answersUri: lz.compressToEncodedURIComponent(
        JSON.stringify(questionnaireAnswers)
      ),
      verticalId,
      affiliateId,
      affiliateSource,
      ...(questionnaireAnswers?.variant
        ? { variant: String(questionnaireAnswers.variant) }
        : {}),
      ...(emailValue ? { email: emailValue } : {}),
    });

    const shareUrl = getShareUrl({
      questionnaireAnswers,
      verticalId,
      seedId: data.data.id,
      utmSource: affiliateSource,
      useStaging: true,
    });

    if (!emailValue && shareUrl) {
      navigator.clipboard.writeText(shareUrl);
    }

    toast(
      !emailValue
        ? t(
            'affiliate.sendToCustomer.modal.toast.message.copyLink',
            'Link copied successfully'
          )
        : t(
            'affiliate.sendToCustomer.modal.toast.message.emailSent',
            'Email sent successfully'
          ),
      { type: 'success' }
    );
    setIsOpen(false);
  };

  const handleCopyToClipboard = async () => {
    getUrl();
  };

  const handleSendToEmail = (e: FormEvent) => {
    e.preventDefault();

    if (invalidEmail || isLoading) {
      return;
    }

    getUrl(email);
  };

  return (
    <>
      <RegularModal
        title={
          <h3 className="p-h3">
            {t('affiliate.sendToCustomer.modal.title', 'Send to customer')}
          </h3>
        }
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setEmail('');
        }}
        classNames={{
          body: 'wmx5 w100',
        }}
      >
        <form className="mx16 mb16" onSubmit={handleSendToEmail}>
          <div className="mb24 d-flex jc-center">
            <img src={illustration} alt="" width={164} />
          </div>

          <p className="p-p mb16">
            {t(
              'affiliate.sendToCustomer.modal.description',
              'A link to this page will be send to the customer by email. The questionnaire will be pre-filled.'
            )}
          </p>

          <Input
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            value={email ?? ''}
            placeholder={t(
              'affiliate.sendToCustomer.modal.input.placeholder',
              'E-mail address'
            )}
            className="mb24"
            disabled={!!isLoading}
          />
          {error && (
            <InformationBox variant="error" className="mb16">
              {t(
                'affiliate.sendToCustomer.modal.buttons.error',
                'An error has occurred, please try again. If the problem persists, please contact our support team.'
              )}
            </InformationBox>
          )}
          <Button
            className="w100 mb8"
            type="submit"
            disabled={invalidEmail || !!isLoading}
          >
            {t(
              'affiliate.sendToCustomer.modal.buttons.sendEmail',
              'Send email'
            )}
          </Button>

          <Button
            className="w100"
            type="button"
            onClick={handleCopyToClipboard}
            variant="filledGray"
            disabled={!!isLoading}
          >
            {t('affiliate.sendToCustomer.modal.buttons.copyLink', 'Copy link')}
          </Button>
        </form>
      </RegularModal>

      <Button
        type="button"
        variant="textColor"
        onClick={() => setIsOpen(true)}
        className="tc-grey-900"
      >
        {t('affiliate.sendToCustomer.button.triggerModal', 'Send to customer')}
      </Button>
    </>
  );
};
